import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { subscriptionGuard } from './core/guards/subscription-guard';
import { guestGuard } from './core/guards/guest-guard';
import { permissionGuard } from './core/guards/permission-guard';
import { dashboardGuard } from './core/guards/dashboard-guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full'
  },
  {
    path: '',
    loadComponent: () =>
      import('../public-app/layouts/public-layout/public-layout').then(m => m.PublicLayout),
    children: [
      // ── Auth pages only ───────────────────────────────────────
      {
        path: 'auth',
        canActivate: [guestGuard],
        children: [
          {
            path: 'login',
            loadComponent: () =>
              import('./features/authentication/pages/login/login').then(m => m.Login)
          },
          {
            path: 'register',
            loadComponent: () =>
              import('./features/authentication/pages/register/register').then(m => m.Register)
          },
          {
            path: 'register/driver',
            loadComponent: () =>
              import('./features/authentication/pages/driver-register/driver-register').then(m => m.DriverRegister)
          },
          {
            path: 'register/vendor',
            loadComponent: () =>
              import('./features/authentication/pages/vendor-register/vendor-register').then(m => m.VendorRegister)
          },
          {
            path: 'register/customer',
            loadComponent: () =>
              import('./features/authentication/pages/customer-register/customer-register').then(m => m.CustomerRegister)
          },
          {
            path: 'forgot-password',
            loadComponent: () =>
              import('./features/authentication/pages/forgot-password/forgot-password').then(
                m => m.ForgotPassword
              )
          },
          {
            path: 'reset-password',
            loadComponent: () =>
              import('./features/authentication/pages/reset-password/reset-password').then(
                m => m.ResetPassword
              )
          }
        ]
      }
    ]
  },
  {
    path: 'payment/moneris',
    loadComponent: () =>
      import('./features/payment/pages/moneris-payment/moneris-payment').then(m => m.MonerisPaymentPage)
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [subscriptionGuard],
    loadComponent: () =>
      import('./layouts/main-layout/main-layout').then(m => m.MainLayout),
    children: [
      // Common routes (all roles)
      {
        path: 'dashboard',
        canActivate: [dashboardGuard],
        loadComponent: () =>
          import('./features/dashboard/pages/dashboard/dashboard').then(m => m.Dashboard)
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/pages/profile-wrapper/profile-wrapper').then(m => m.ProfileWrapper)
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/notification/pages/notification-wrapper/notification-wrapper').then(m => m.NotificationWrapper)
      },
      {
        path: 'change-password',
        loadComponent: () =>
          import('./features/authentication/pages/change-password/change-password').then(m => m.ChangePassword)
      },

      // Vendor routes
      {
        path: 'transactions',
        loadComponent: () =>
          import('./features/transaction/pages/transaction/transaction').then(m => m.TransactionPage)
      },
      {
        path: 'payouts',
        loadComponent: () =>
          import('./features/payout/pages/payout/payout').then(m => m.PayoutPage)
      },
      {
        path: 'payouts/:id',
        loadComponent: () =>
          import('./features/payout/pages/payout-detail/payout-detail').then(m => m.PayoutDetailPage)
      },
      {
        path: 'invoice',
        loadComponent: () =>
          import('./features/invoice/pages/invoice/invoice').then(m => m.InvoicePage)
      },
      {
        path: 'invoice/:id',
        loadComponent: () =>
          import('./features/invoice/pages/invoice-detail/invoice-detail').then(m => m.InvoiceDetailPage)
      },
      {
        path: 'subscription',
        loadComponent: () =>
          import('./features/subscription/pages/subscription/subscription').then(m => m.SubscriptionPage)
      },

      // Driver routes
      {
        path: 'collect-payments',
        loadComponent: () =>
          import('./features/collect-payments/pages/collect-payments/collect-payments').then(m => m.CollectPaymentsPage)
      },

      // ── Admin / Staff permission-guarded routes ────────────────
      {
        path: 'analytics',
        canActivate: [permissionGuard('analytics')],
        loadComponent: () =>
          import('./features/analytics/pages/analytics/analytics').then(m => m.AnalyticsPage)
      },
      {
        path: 'staff',
        canActivate: [permissionGuard('staff')],
        loadComponent: () =>
          import('./features/staff/pages/staff/staff').then(m => m.StaffPage)
      },
      {
        path: 'staff/add',
        canActivate: [permissionGuard('staff')],
        loadComponent: () =>
          import('./features/staff/pages/staff-form/staff-form').then(m => m.StaffFormPage)
      },
      {
        path: 'staff/:id/edit',
        canActivate: [permissionGuard('staff')],
        loadComponent: () =>
          import('./features/staff/pages/staff-form/staff-form').then(m => m.StaffFormPage)
      },
      {
        path: 'staff/:id/permissions',
        canActivate: [permissionGuard('staff')],
        loadComponent: () =>
          import('./features/staff/pages/staff-permissions/staff-permissions').then(m => m.StaffPermissionsPage)
      },
      {
        path: 'approval-requests',
        canActivate: [permissionGuard('approval_requests')],
        loadComponent: () =>
          import('./features/approval-requests/pages/approval-requests/approval-requests').then(m => m.ApprovalRequestsPage)
      },
      {
        path: 'approval-requests/:type/:id',
        canActivate: [permissionGuard('approval_requests')],
        loadComponent: () =>
          import('./features/approval-requests/pages/approval-request-detail/approval-request-detail').then(m => m.ApprovalRequestDetailPage)
      },
      {
        path: 'vendors',
        canActivate: [permissionGuard('vendors')],
        loadComponent: () =>
          import('./features/vendors/pages/vendors/vendors').then(m => m.VendorsPage)
      },
      {
        path: 'vendors/add',
        canActivate: [permissionGuard('vendors')],
        loadComponent: () =>
          import('./features/vendors/pages/vendor-form/vendor-form').then(m => m.VendorFormPage)
      },
      {
        path: 'vendors/:id',
        canActivate: [permissionGuard('vendors')],
        loadComponent: () =>
          import('./features/vendors/pages/vendor-details/vendor-details').then(m => m.VendorDetailsPage)
      },
      {
        path: 'vendors/:id/edit',
        canActivate: [permissionGuard('vendors')],
        loadComponent: () =>
          import('./features/vendors/pages/vendor-form/vendor-form').then(m => m.VendorFormPage)
      },
      {
        path: 'delivery-partners',
        canActivate: [permissionGuard('delivery_partners')],
        loadComponent: () =>
          import('./features/delivery-partners/pages/delivery-partners/delivery-partners').then(m => m.DeliveryPartnersPage)
      },
      {
        path: 'delivery-partners/add',
        canActivate: [permissionGuard('delivery_partners')],
        loadComponent: () =>
          import('./features/delivery-partners/pages/partner-form/partner-form').then(m => m.PartnerFormPage)
      },
      {
        path: 'delivery-partners/:id',
        canActivate: [permissionGuard('delivery_partners')],
        loadComponent: () =>
          import('./features/delivery-partners/pages/partner-details/partner-details').then(m => m.PartnerDetailsPage)
      },
      {
        path: 'delivery-partners/:id/edit',
        canActivate: [permissionGuard('delivery_partners')],
        loadComponent: () =>
          import('./features/delivery-partners/pages/partner-form/partner-form').then(m => m.PartnerFormPage)
      },
      {
        path: 'total-orders',
        canActivate: [permissionGuard('orders')],
        loadComponent: () =>
          import('./features/total-orders/pages/total-orders/total-orders').then(m => m.TotalOrdersPage)
      },
      {
        path: 'total-orders/:id',
        canActivate: [permissionGuard('orders')],
        loadComponent: () =>
          import('./features/total-orders/pages/total-order-detail/total-order-detail').then(m => m.TotalOrderDetailPage)
      },
      {
        path: 'add-expense',
        canActivate: [permissionGuard('expenses')],
        loadComponent: () =>
          import('./features/add-expense/pages/add-expense/add-expense').then(m => m.AddExpensePage)
      },
      {
        path: 'add-expense/new',
        canActivate: [permissionGuard('expenses')],
        loadComponent: () =>
          import('./features/add-expense/pages/expense-form/expense-form').then(m => m.ExpenseFormPage)
      },
      {
        path: 'add-expense/:id/edit',
        canActivate: [permissionGuard('expenses')],
        loadComponent: () =>
          import('./features/add-expense/pages/expense-form/expense-form').then(m => m.ExpenseFormPage)
      },
      {
        path: 'gst-management',
        canActivate: [permissionGuard('gst')],
        loadComponent: () =>
          import('./features/gst-management/pages/gst-management/gst-management').then(m => m.GstManagementPage)
      },
      {
        path: 'gst-management/:id',
        canActivate: [permissionGuard('gst')],
        loadComponent: () =>
          import('./features/gst-management/pages/gst-record-detail/gst-record-detail').then(m => m.GstRecordDetailPage)
      },
      {
        path: 'ledger',
        canActivate: [permissionGuard('ledger')],
        loadComponent: () =>
          import('./features/ledger/pages/ledger/ledger').then(m => m.LedgerPage)
      },
      {
        path: 'payout-dashboard',
        canActivate: [permissionGuard('payout_dashboard')],
        loadComponent: () =>
          import('./features/payout-dashboard/pages/payout-dashboard/payout-dashboard').then(m => m.PayoutDashboardPage)
      },
      {
        path: 'payout-batches',
        canActivate: [permissionGuard('payout_batches')],
        loadComponent: () =>
          import('./features/payout-dashboard/pages/payout-batches/payout-batches').then(m => m.PayoutBatchesPage)
      },
      {
        path: 'payout-batch-detail/:id',
        canActivate: [permissionGuard('payout_batches')],
        loadComponent: () =>
          import('./features/payout-dashboard/pages/payout-batch-detail/payout-batch-detail').then(m => m.PayoutBatchDetailPage)
      },
      {
        path: 'vendor-payout-detail/:batchId/:vendorId',
        canActivate: [permissionGuard('payout_dashboard')],
        loadComponent: () =>
          import('./features/payout-dashboard/pages/vendor-payout-detail/vendor-payout-detail').then(m => m.VendorPayoutDetailPage)
      },
      {
        path: 'payout-analytics',
        canActivate: [permissionGuard('payout_analytics')],
        loadComponent: () =>
          import('./features/payout-analytics/pages/payout-analytics/payout-analytics').then(m => m.PayoutAnalyticsPage)
      },
      {
        path: 'payout-settings',
        canActivate: [permissionGuard('payout_settings')],
        loadComponent: () =>
          import('./features/payout-settings/pages/payout-settings/payout-settings').then(m => m.PayoutSettingsPage)
      },
      {
        path: 'subscription-masters',
        canActivate: [permissionGuard('submasters')],
        loadComponent: () =>
          import('./features/subscription-masters/pages/subscription-masters/subscription-masters').then(m => m.SubscriptionMastersPage)
      },
      {
        path: 'subscription-masters/add',
        canActivate: [permissionGuard('submasters')],
        loadComponent: () =>
          import('./features/subscription-masters/pages/subscription-form/subscription-form').then(m => m.SubscriptionFormPage)
      },
      {
        path: 'subscription-masters/:id/edit',
        canActivate: [permissionGuard('submasters')],
        loadComponent: () =>
          import('./features/subscription-masters/pages/subscription-form/subscription-form').then(m => m.SubscriptionFormPage)
      },
      {
        path: 'fees-master',
        canActivate: [permissionGuard('fees_master')],
        loadComponent: () =>
          import('./features/fees-master/pages/fees-master/fees-master').then(m => m.FeesMasterPage)
      },
      {
        path: 'rbc-payedge',
        loadComponent: () =>
          import('./features/rbc-payedge/pages/rbc-payedge/rbc-payedge').then(m => m.RbcPayedgePage)
      }
    ]
  },
  {
    path: '.well-known/apple-app-site-association',
    loadComponent: () =>
      import('./features/well-known/apple-app-site-association/apple-app-site-association').then(m => m.AppleAppSiteAssociationComponent)
  },
  {
    path: '**',
    loadComponent: () =>
      import('./shared/components/not-found/not-found').then(m => m.NotFound)
  }
];
